import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { FileText, BarChart3, Receipt, ShieldCheck, Download, Copy, Plus, RotateCcw, History } from 'lucide-react';
import { theme, cardStyle, inputStyle, labelStyle, tdStyle, thStyle, btnStyle, statCard } from '../theme';

const fmtINR = n => `₹${(Number(n)||0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = d => d ? new Date(d).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}) : '—';
const thisMonth = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`; };

export default function TaxCompliancePage({ API_BASE, currentUser }) {
    const [tab, setTab] = useState('einvoice');

    const tabBtn = (k, Icon, label) => (
        <button onClick={()=>setTab(k)} style={{padding:'9px 14px', borderRadius:'8px', border:'none', cursor:'pointer', fontWeight:'600', fontSize:'0.82rem',
            backgroundColor: tab===k ? theme.primary : '#e9ecef', color: tab===k ? 'white' : '#555',
            display:'inline-flex', alignItems:'center', gap:'6px'}}>
            <Icon size={13}/>{label}
        </button>
    );

    return (
        <div>
            <div style={{marginBottom:'16px'}}>
                <h2 style={{margin:0, color:theme.navy, fontSize:'1.4rem', fontWeight:'700'}}>Tax & Compliance</h2>
                <p style={{margin:'2px 0 0', color:theme.textMuted, fontSize:'0.85rem'}}>E-Invoice · GSTR-1 · GSTR-2 · ITC Ledger · TDS · Audit Trail</p>
            </div>
            <div style={{display:'flex', gap:'6px', marginBottom:'18px', flexWrap:'wrap'}}>
                {tabBtn('einvoice', FileText,     'E-Invoice')}
                {tabBtn('gstr1',    BarChart3,    'GSTR-1')}
                {tabBtn('gstr2',    Receipt,      'GSTR-2 / Purchases')}
                {tabBtn('itc',      Receipt,      'ITC Ledger')}
                {tabBtn('tds',      FileText,     'TDS')}
                {tabBtn('audit',    History,      'GST Audit Log')}
            </div>

            {tab==='einvoice' && <EInvoiceTab API_BASE={API_BASE} currentUser={currentUser}/>}
            {tab==='gstr1'    && <Gstr1Tab    API_BASE={API_BASE}/>}
            {tab==='gstr2'    && <Gstr2Tab    API_BASE={API_BASE}/>}
            {tab==='itc'      && <ItcTab      API_BASE={API_BASE}/>}
            {tab==='tds'      && <TdsTab      API_BASE={API_BASE} currentUser={currentUser}/>}
            {tab==='audit'    && <AuditTab    API_BASE={API_BASE}/>}
        </div>
    );
}

// ── E-Invoice ────────────────────────────────────────────────────────
function EInvoiceTab({ API_BASE, currentUser }) {
    const [bills, setBills] = useState([]);
    const [einvoices, setEinvoices] = useState([]);
    const [selected, setSelected] = useState(null);
    const [payload, setPayload] = useState(null);
    const [irnForm, setIrnForm] = useState(null);

    const refresh = useCallback(async () => {
        try {
            const [b, e] = await Promise.all([
                axios.get(`${API_BASE}/bills?billType=invoice&status=finalized&limit=500`).catch(()=>({data:[]})),
                axios.get(`${API_BASE}/tax/einvoices?limit=500`).catch(()=>({data:[]})),
            ]);
            setBills(b.data); setEinvoices(e.data);
        } catch {}
    }, [API_BASE]);
    useEffect(() => { refresh(); }, [refresh]);

    const einvoiceByBill = Object.fromEntries(einvoices.map(e => [String(e.billId), e]));

    const showPayload = async (billId) => {
        try {
            const r = await axios.get(`${API_BASE}/tax/einvoice/${billId}/payload`);
            setSelected(billId);
            setPayload(r.data);
        } catch (e) { alert(e.response?.data?.error || 'Failed'); }
    };
    const generate = async (billId) => {
        try {
            await axios.post(`${API_BASE}/tax/einvoice/${billId}/generate`, { createdBy: currentUser?.username });
            refresh();
        } catch (e) { alert(e.response?.data?.error || 'Failed'); }
    };
    const saveIrn = async () => {
        try {
            await axios.put(`${API_BASE}/tax/einvoice/${irnForm.billId}/irn`, irnForm);
            setIrnForm(null); refresh();
        } catch (e) { alert(e.response?.data?.error || 'Failed'); }
    };
    const cancelEI = async (billId) => {
        const reason = window.prompt('Cancellation reason (required within 24h):'); if (!reason) return;
        await axios.put(`${API_BASE}/tax/einvoice/${billId}/cancel`, { reason, cancelledBy: currentUser?.username });
        refresh();
    };

    return (
        <div>
            <div style={cardStyle}>
                <h3 style={{margin:'0 0 12px', color:theme.navy}}>Finalized Invoices ({bills.length})</h3>
                <p style={{fontSize:'0.82rem', color:theme.textMuted, margin:'0 0 12px'}}>Generate the GSTN-schema JSON payload, submit manually to your IRP portal / GSP, then record the returned IRN + QR here.</p>
                <table style={{width:'100%', borderCollapse:'collapse'}}>
                    <thead><tr>
                        <th style={thStyle}>Invoice #</th><th style={thStyle}>Date</th>
                        <th style={thStyle}>Amount</th><th style={thStyle}>E-Invoice Status</th>
                        <th style={thStyle}>IRN</th><th style={thStyle}>Actions</th>
                    </tr></thead>
                    <tbody>
                        {bills.map(b => {
                            const ei = einvoiceByBill[b._id];
                            return (
                                <tr key={b._id}>
                                    <td style={tdStyle}><strong style={{fontFamily:'monospace', fontSize:'0.82rem'}}>{b.billNumber || b.invoiceNo}</strong></td>
                                    <td style={tdStyle}>{fmtDate(b.date)}</td>
                                    <td style={tdStyle}>{fmtINR(b.totalAmount)}</td>
                                    <td style={tdStyle}>{ei ? <span style={{padding:'2px 8px', borderRadius:'10px', fontSize:'0.72rem', fontWeight:'700', backgroundColor:{generated:'#fef3c7',registered:'#d1fae5',cancelled:'#fee2e2',failed:'#fee2e2'}[ei.status]||'#e5e7eb', color:{generated:'#92400e',registered:'#065f46',cancelled:'#991b1b',failed:'#991b1b'}[ei.status]||'#374151'}}>{ei.status}</span> : <em style={{color:'#cbd5e1'}}>not generated</em>}</td>
                                    <td style={tdStyle}><code style={{fontSize:'0.72rem'}}>{ei?.irn ? ei.irn.slice(0,18)+'…' : '—'}</code></td>
                                    <td style={tdStyle}>
                                        <div style={{display:'flex', gap:'4px', flexWrap:'wrap'}}>
                                            <button onClick={()=>showPayload(b._id)} style={{padding:'3px 10px', backgroundColor:theme.info, color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'0.72rem'}}>Payload</button>
                                            {!ei && <button onClick={()=>generate(b._id)} style={{padding:'3px 10px', backgroundColor:theme.primary, color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'0.72rem', fontWeight:'700'}}>Generate</button>}
                                            {ei?.status === 'generated' && <button onClick={()=>setIrnForm({ billId: b._id, irn:'', ackNo:'', ackDate: new Date().toISOString().slice(0,10) })} style={{padding:'3px 10px', backgroundColor:theme.success, color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'0.72rem', fontWeight:'700'}}>Record IRN</button>}
                                            {ei?.status === 'registered' && <button onClick={()=>cancelEI(b._id)} style={{padding:'3px 10px', backgroundColor:theme.danger, color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'0.72rem'}}>Cancel IRN</button>}
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                        {bills.length === 0 && <tr><td colSpan="6" style={{...tdStyle, textAlign:'center', color:theme.textMuted, padding:'30px'}}>No finalized invoices</td></tr>}
                    </tbody>
                </table>
            </div>

            {selected && payload && (
                <Modal onClose={()=>{setSelected(null); setPayload(null);}} title="E-Invoice Payload (GSTN Schema v1.1)" wide>
                    <p style={{fontSize:'0.82rem', color:theme.textMuted, margin:'0 0 10px'}}>Copy this JSON and upload to your IRP portal (e.g. einvoice1.gst.gov.in) or your GSP (ClearTax, IRIS, Masters India).</p>
                    <pre style={{backgroundColor:'#0f172a', color:'#e2e8f0', padding:'14px', borderRadius:'8px', fontSize:'0.76rem', maxHeight:'450px', overflow:'auto'}}>{JSON.stringify(payload, null, 2)}</pre>
                    <button onClick={()=>{navigator.clipboard.writeText(JSON.stringify(payload, null, 2)); alert('Copied');}} style={{...btnStyle, marginTop:'10px'}}><Copy size={14} style={{verticalAlign:'middle',marginRight:'6px'}}/>Copy JSON</button>
                </Modal>
            )}

            {irnForm && (
                <Modal onClose={()=>setIrnForm(null)} title="Record IRN & QR from IRP Response">
                    <label style={labelStyle}>IRN (64-char hash)</label>
                    <input value={irnForm.irn} onChange={e=>setIrnForm({...irnForm, irn:e.target.value})} style={{...inputStyle, fontFamily:'monospace', fontSize:'0.78rem'}}/>
                    <label style={labelStyle}>Ack No</label>
                    <input value={irnForm.ackNo} onChange={e=>setIrnForm({...irnForm, ackNo:e.target.value})} style={inputStyle}/>
                    <label style={labelStyle}>Ack Date</label>
                    <input type="date" value={irnForm.ackDate} onChange={e=>setIrnForm({...irnForm, ackDate:e.target.value})} style={inputStyle}/>
                    <label style={labelStyle}>Signed QR Code (base64)</label>
                    <textarea rows={3} value={irnForm.signedQrCode||''} onChange={e=>setIrnForm({...irnForm, signedQrCode:e.target.value})} style={{...inputStyle, resize:'vertical', fontSize:'0.74rem'}}/>
                    <button onClick={saveIrn} style={{...btnStyle, marginTop:'10px'}}>Save</button>
                </Modal>
            )}
        </div>
    );
}

// ── GSTR-1 ───────────────────────────────────────────────────────────
function Gstr1Tab({ API_BASE }) {
    const [month, setMonth] = useState(thisMonth());
    const [report, setReport] = useState(null);
    const [busy, setBusy] = useState(false);

    const run = async () => {
        setBusy(true);
        try { const r = await axios.get(`${API_BASE}/tax/gstr1?month=${month}`); setReport(r.data); }
        catch (e) { alert(e.response?.data?.error || 'Failed'); }
        setBusy(false);
    };
    const dlCsv = () => { window.open(`${API_BASE}/tax/gstr1?month=${month}&format=csv`, '_blank'); };

    return (
        <div>
            <div style={{...cardStyle, display:'flex', alignItems:'end', gap:'10px', marginBottom:'14px'}}>
                <div><label style={labelStyle}>Month</label><input type="month" value={month} onChange={e=>setMonth(e.target.value)} style={{...inputStyle, marginBottom:0, width:'180px'}}/></div>
                <button onClick={run} disabled={busy} style={{...btnStyle, marginTop:0, padding:'10px 18px'}}>{busy ? 'Generating…' : 'Generate GSTR-1'}</button>
                {report && <button onClick={dlCsv} style={{padding:'10px 18px', backgroundColor:theme.success, color:'white', border:'none', borderRadius:'8px', cursor:'pointer', fontWeight:'700', fontSize:'0.85rem'}}><Download size={14} style={{verticalAlign:'middle', marginRight:'6px'}}/>Export CSV</button>}
            </div>

            {report && (<>
                <div style={{display:'grid', gridTemplateColumns:'repeat(6,1fr)', gap:'12px', marginBottom:'18px'}}>
                    {[
                        {l:'Invoices', v:report.totals.invoices},
                        {l:'Taxable', v:fmtINR(report.totals.taxable)},
                        {l:'CGST', v:fmtINR(report.totals.cgst)},
                        {l:'SGST', v:fmtINR(report.totals.sgst)},
                        {l:'IGST', v:fmtINR(report.totals.igst)},
                        {l:'Total', v:fmtINR(report.totals.total)},
                    ].map((s,i) => (
                        <div key={i} style={statCard}>
                            <span style={{fontSize:'0.74rem', color:theme.textMuted, fontWeight:'600', textTransform:'uppercase'}}>{s.l}</span>
                            <h3 style={{margin:'4px 0 0', color:theme.navy, fontSize:'1.1rem', fontWeight:'700'}}>{s.v}</h3>
                        </div>
                    ))}
                </div>

                <Section title={`B2B (Registered, with GSTIN) — ${report.b2b.length}`} rows={report.b2b} cols={['invoiceNumber','invoiceDate','gstin','recipientName','placeOfSupply','taxableValue','cgst','sgst','igst','total']}/>
                <Section title={`B2CL (Unregistered, Inter-state > ₹2.5L) — ${report.b2cl.length}`} rows={report.b2cl} cols={['invoiceNumber','invoiceDate','recipientName','taxableValue','igst','total']}/>
                <Section title={`B2CS (Unregistered, Small) — ${report.b2cs.length}`} rows={report.b2cs} cols={['invoiceNumber','invoiceDate','recipientName','taxableValue','cgst','sgst','total']}/>
                <Section title={`CDNR (Credit Notes, Registered) — ${report.cdnr.length}`} rows={report.cdnr} cols={['cnNumber','cnDate','origInvoice','gstin','taxableValue','cgst','sgst','total']}/>
                <Section title={`CDNUR (Credit Notes, Unregistered) — ${report.cdnur.length}`} rows={report.cdnur} cols={['cnNumber','cnDate','origInvoice','taxableValue','cgst','sgst','total']}/>
                <Section title={`HSN Summary — ${report.hsn.length}`} rows={report.hsn} cols={['hsn','rate','qty','taxableValue','cgst','sgst','igst','total']}/>
            </>)}
        </div>
    );
}

// ── GSTR-2 / Purchase Register ───────────────────────────────────────
function Gstr2Tab({ API_BASE }) {
    const [month, setMonth] = useState(thisMonth());
    const [report, setReport] = useState(null);
    const run = async () => {
        try { const r = await axios.get(`${API_BASE}/tax/gstr2?month=${month}`); setReport(r.data); }
        catch (e) { alert(e.response?.data?.error || 'Failed'); }
    };

    return (
        <div>
            <div style={{...cardStyle, display:'flex', alignItems:'end', gap:'10px', marginBottom:'14px'}}>
                <div><label style={labelStyle}>Month</label><input type="month" value={month} onChange={e=>setMonth(e.target.value)} style={{...inputStyle, marginBottom:0, width:'180px'}}/></div>
                <button onClick={run} style={{...btnStyle, marginTop:0, padding:'10px 18px'}}>Generate Purchase Register</button>
            </div>
            {report && (<>
                <div style={{display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:'12px', marginBottom:'18px'}}>
                    {[
                        {l:'Suppliers', v:report.suppliers.length},
                        {l:'Taxable', v:fmtINR(report.totals.taxable)},
                        {l:'CGST', v:fmtINR(report.totals.cgst)},
                        {l:'SGST', v:fmtINR(report.totals.sgst)},
                        {l:'Total', v:fmtINR(report.totals.total)},
                    ].map((s,i) => (
                        <div key={i} style={statCard}>
                            <span style={{fontSize:'0.74rem', color:theme.textMuted, fontWeight:'600', textTransform:'uppercase'}}>{s.l}</span>
                            <h3 style={{margin:'4px 0 0', color:theme.navy, fontSize:'1.1rem', fontWeight:'700'}}>{s.v}</h3>
                        </div>
                    ))}
                </div>
                <Section title="By Supplier" rows={report.suppliers} cols={['supplierName','gstin','taxable','cgst','sgst','total']}/>
                <Section title="HSN/Item Summary" rows={report.hsn} cols={['item','rate','qty','taxable','tax']}/>
            </>)}
        </div>
    );
}

// ── ITC Ledger ───────────────────────────────────────────────────────
function ItcTab({ API_BASE }) {
    const [ledger, setLedger] = useState(null);
    const [month, setMonth] = useState('');
    const [selected, setSelected] = useState(new Set());

    const refresh = useCallback(async () => {
        try { const r = await axios.get(`${API_BASE}/tax/itc${month?`?month=${month}`:''}`); setLedger(r.data); } catch {}
    }, [API_BASE, month]);
    useEffect(() => { refresh(); }, [refresh]);

    const toggle = (id) => {
        const n = new Set(selected);
        if (n.has(id)) n.delete(id); else n.add(id);
        setSelected(n);
    };
    const claim = async () => {
        const m = window.prompt('Claim in month (YYYY-MM):', thisMonth()); if (!m) return;
        await axios.put(`${API_BASE}/tax/itc/claim`, { ids: Array.from(selected), claimedMonth: m });
        setSelected(new Set()); refresh();
    };

    if (!ledger) return <div style={{...cardStyle, textAlign:'center', color:theme.textMuted, padding:'40px'}}>Loading…</div>;

    return (
        <div>
            <div style={{display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'12px', marginBottom:'14px'}}>
                {[
                    {l:'Eligible ITC', v:ledger.eligible.total, c:theme.success},
                    {l:'Claimed ITC', v:ledger.claimed.total, c:theme.info},
                    {l:'Pending', v:ledger.pending.total, c:theme.warning},
                    {l:'Reversed', v:ledger.reversed.total, c:theme.danger},
                ].map((s,i) => (
                    <div key={i} style={statCard}>
                        <span style={{fontSize:'0.74rem', color:theme.textMuted, fontWeight:'600', textTransform:'uppercase'}}>{s.l}</span>
                        <h3 style={{margin:'4px 0 0', color:s.c, fontSize:'1.2rem', fontWeight:'700'}}>{fmtINR(s.v)}</h3>
                    </div>
                ))}
            </div>

            <div style={cardStyle}>
                <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'12px'}}>
                    <h3 style={{margin:0, color:theme.navy}}>ITC Entries ({ledger.entries.length})</h3>
                    <div style={{display:'flex', gap:'8px'}}>
                        <input type="month" value={month} onChange={e=>setMonth(e.target.value)} style={{...inputStyle, marginBottom:0, width:'160px'}}/>
                        {selected.size > 0 && <button onClick={claim} style={{padding:'8px 14px', backgroundColor:theme.success, color:'white', border:'none', borderRadius:'8px', cursor:'pointer', fontSize:'0.82rem', fontWeight:'700'}}>Mark {selected.size} as Claimed</button>}
                    </div>
                </div>
                <table style={{width:'100%', borderCollapse:'collapse', fontSize:'0.84rem'}}>
                    <thead><tr>
                        <th style={thStyle}></th><th style={thStyle}>Date</th><th style={thStyle}>Source</th>
                        <th style={thStyle}>Supplier</th><th style={thStyle}>GSTIN</th>
                        <th style={thStyle}>Taxable</th><th style={thStyle}>CGST</th><th style={thStyle}>SGST</th><th style={thStyle}>IGST</th>
                        <th style={thStyle}>Status</th><th style={thStyle}>Claimed Month</th>
                    </tr></thead>
                    <tbody>
                        {ledger.entries.map(e => (
                            <tr key={e._id}>
                                <td style={tdStyle}>{e.status === 'eligible' && <input type="checkbox" checked={selected.has(e._id)} onChange={()=>toggle(e._id)}/>}</td>
                                <td style={tdStyle}>{fmtDate(e.date)}</td>
                                <td style={tdStyle}>{e.sourceRef || e.sourceType}</td>
                                <td style={tdStyle}>{e.supplierName || '—'}</td>
                                <td style={tdStyle}><code style={{fontSize:'0.72rem'}}>{e.supplierGstin || '—'}</code></td>
                                <td style={tdStyle}>{fmtINR(e.taxableValue)}</td>
                                <td style={tdStyle}>{fmtINR(e.cgst)}</td>
                                <td style={tdStyle}>{fmtINR(e.sgst)}</td>
                                <td style={tdStyle}>{fmtINR(e.igst)}</td>
                                <td style={tdStyle}><span style={{padding:'2px 8px', borderRadius:'10px', fontSize:'0.7rem', fontWeight:'700', backgroundColor:{eligible:'#d1fae5',claimed:'#dbeafe',pending:'#fef3c7',reversed:'#fee2e2'}[e.status], color:{eligible:'#065f46',claimed:'#1e40af',pending:'#92400e',reversed:'#991b1b'}[e.status]}}>{e.status}</span></td>
                                <td style={tdStyle}>{e.claimedMonth || '—'}</td>
                            </tr>
                        ))}
                        {ledger.entries.length === 0 && <tr><td colSpan="11" style={{...tdStyle, textAlign:'center', color:theme.textMuted, padding:'30px'}}>No ITC entries — post GRNs to build credit</td></tr>}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ── TDS ──────────────────────────────────────────────────────────────
function TdsTab({ API_BASE, currentUser }) {
    const [list, setList] = useState([]);
    const [summary, setSummary] = useState(null);
    const [editing, setEditing] = useState(null);

    const refresh = useCallback(async () => {
        try {
            const [l, s] = await Promise.all([
                axios.get(`${API_BASE}/tax/tds`),
                axios.get(`${API_BASE}/tax/tds/summary`),
            ]);
            setList(l.data); setSummary(s.data);
        } catch {}
    }, [API_BASE]);
    useEffect(() => { refresh(); }, [refresh]);

    const empty = () => ({ section:'194J', payeeName:'', payeePAN:'', grossAmount:0, tdsRate:10, tdsAmount:0, paymentDate:new Date().toISOString().slice(0,10) });
    const save = async () => {
        try {
            if (editing._id) await axios.put(`${API_BASE}/tax/tds/${editing._id}`, editing);
            else await axios.post(`${API_BASE}/tax/tds`, editing);
            setEditing(null); refresh();
        } catch (e) { alert(e.response?.data?.error || 'Failed'); }
    };

    return (
        <div>
            {summary && (
                <div style={{display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'12px', marginBottom:'14px'}}>
                    {[
                        {l:'Entries', v:summary.count},
                        {l:'Total TDS', v:fmtINR(summary.totalTds), c:theme.info},
                        {l:'Total Gross', v:fmtINR(summary.totalGross)},
                        {l:'By Section', v:Object.keys(summary.bySection).length + ' types'},
                    ].map((s,i) => (
                        <div key={i} style={statCard}>
                            <span style={{fontSize:'0.74rem', color:theme.textMuted, fontWeight:'600', textTransform:'uppercase'}}>{s.l}</span>
                            <h3 style={{margin:'4px 0 0', color:s.c||theme.navy, fontSize:'1.1rem', fontWeight:'700'}}>{s.v}</h3>
                        </div>
                    ))}
                </div>
            )}

            <div style={{display:'flex', justifyContent:'space-between', marginBottom:'10px'}}>
                <h3 style={{margin:0, color:theme.navy}}>TDS Entries ({list.length})</h3>
                <button onClick={()=>setEditing(empty())} style={{...btnStyle, marginTop:0, padding:'8px 14px', fontSize:'0.85rem'}}><Plus size={14} style={{verticalAlign:'middle', marginRight:'4px'}}/>New TDS Entry</button>
            </div>
            <div style={cardStyle}>
                <table style={{width:'100%', borderCollapse:'collapse', fontSize:'0.84rem'}}>
                    <thead><tr>
                        <th style={thStyle}>Date</th><th style={thStyle}>Section</th><th style={thStyle}>Payee</th>
                        <th style={thStyle}>PAN</th><th style={thStyle}>Gross</th><th style={thStyle}>Rate</th>
                        <th style={thStyle}>TDS</th><th style={thStyle}>FY / Quarter</th><th style={thStyle}>Status</th>
                    </tr></thead>
                    <tbody>
                        {list.map(t => (
                            <tr key={t._id}>
                                <td style={tdStyle}>{fmtDate(t.paymentDate)}</td>
                                <td style={tdStyle}><strong>{t.section}</strong></td>
                                <td style={tdStyle}>{t.payeeName}</td>
                                <td style={tdStyle}><code style={{fontSize:'0.76rem'}}>{t.payeePAN || '—'}</code></td>
                                <td style={tdStyle}>{fmtINR(t.grossAmount)}</td>
                                <td style={tdStyle}>{t.tdsRate}%</td>
                                <td style={tdStyle}><strong>{fmtINR(t.tdsAmount)}</strong></td>
                                <td style={tdStyle}>{t.financialYear} · {t.quarter}</td>
                                <td style={tdStyle}><span style={{padding:'2px 8px', borderRadius:'10px', fontSize:'0.7rem', fontWeight:'700', backgroundColor:{pending:'#fef3c7',paid:'#d1fae5',filed:'#dbeafe'}[t.status], color:{pending:'#92400e',paid:'#065f46',filed:'#1e40af'}[t.status]}}>{t.status}</span></td>
                            </tr>
                        ))}
                        {list.length === 0 && <tr><td colSpan="9" style={{...tdStyle, textAlign:'center', color:theme.textMuted, padding:'30px'}}>No TDS entries yet</td></tr>}
                    </tbody>
                </table>
            </div>

            {editing && (
                <Modal onClose={()=>setEditing(null)} title={editing._id?'Edit TDS Entry':'New TDS Entry'}>
                    <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px'}}>
                        <div><label style={labelStyle}>Section</label>
                            <select value={editing.section} onChange={e=>setEditing({...editing, section:e.target.value})} style={inputStyle}>
                                <option value="194H">194H — Commission / Brokerage</option>
                                <option value="194I">194I — Rent</option>
                                <option value="194J">194J — Professional Fees</option>
                                <option value="194Q">194Q — Purchase of Goods</option>
                                <option value="194C">194C — Contractors</option>
                                <option value="other">Other</option>
                            </select>
                        </div>
                        <div><label style={labelStyle}>Payment Date</label><input type="date" value={editing.paymentDate} onChange={e=>setEditing({...editing, paymentDate:e.target.value})} style={inputStyle}/></div>
                        <div style={{gridColumn:'span 2'}}><label style={labelStyle}>Payee Name *</label><input value={editing.payeeName} onChange={e=>setEditing({...editing, payeeName:e.target.value})} style={inputStyle}/></div>
                        <div><label style={labelStyle}>PAN</label><input value={editing.payeePAN} onChange={e=>setEditing({...editing, payeePAN:e.target.value.toUpperCase()})} style={{...inputStyle, fontFamily:'monospace'}}/></div>
                        <div><label style={labelStyle}>GSTIN</label><input value={editing.payeeGSTIN||''} onChange={e=>setEditing({...editing, payeeGSTIN:e.target.value})} style={inputStyle}/></div>
                        <div><label style={labelStyle}>Gross Amount ₹</label><input type="number" value={editing.grossAmount} onChange={e=>setEditing({...editing, grossAmount:Number(e.target.value)||0, tdsAmount: Math.round((Number(e.target.value)||0)*Number(editing.tdsRate)/100*100)/100})} style={inputStyle}/></div>
                        <div><label style={labelStyle}>TDS Rate %</label><input type="number" value={editing.tdsRate} onChange={e=>setEditing({...editing, tdsRate:Number(e.target.value)||0, tdsAmount: Math.round(Number(editing.grossAmount)*Number(e.target.value)/100*100)/100})} style={inputStyle}/></div>
                        <div><label style={labelStyle}>TDS Amount</label><input type="number" value={editing.tdsAmount} onChange={e=>setEditing({...editing, tdsAmount:Number(e.target.value)||0})} style={inputStyle}/></div>
                        <div><label style={labelStyle}>Challan No</label><input value={editing.challanNo||''} onChange={e=>setEditing({...editing, challanNo:e.target.value})} style={inputStyle}/></div>
                    </div>
                    <button onClick={save} style={{...btnStyle, marginTop:'10px'}}>Save</button>
                </Modal>
            )}
        </div>
    );
}

// ── Audit Log ────────────────────────────────────────────────────────
function AuditTab({ API_BASE }) {
    const [list, setList] = useState([]);
    const [filter, setFilter] = useState({ entityType:'', changedBy:'', field:'' });
    const refresh = useCallback(async () => {
        try {
            const p = new URLSearchParams();
            Object.entries(filter).forEach(([k,v]) => v && p.append(k, v));
            const r = await axios.get(`${API_BASE}/tax/audit?${p.toString()}`);
            setList(r.data);
        } catch {}
    }, [API_BASE, filter]);
    useEffect(() => { refresh(); }, [refresh]);

    return (
        <div>
            <div style={{...cardStyle, display:'flex', gap:'10px', marginBottom:'14px', flexWrap:'wrap'}}>
                <select value={filter.entityType} onChange={e=>setFilter(f=>({...f, entityType:e.target.value}))} style={{...inputStyle, marginBottom:0, width:'auto'}}>
                    <option value="">All entities</option>
                    <option value="Bill">Bill</option>
                    <option value="PharmacyItem">PharmacyItem</option>
                    <option value="ClinicSettings">ClinicSettings</option>
                    <option value="Patient">Patient</option>
                </select>
                <input placeholder="Changed by" value={filter.changedBy} onChange={e=>setFilter(f=>({...f, changedBy:e.target.value}))} style={{...inputStyle, marginBottom:0, flex:'1 1 180px'}}/>
                <input placeholder="Field name" value={filter.field} onChange={e=>setFilter(f=>({...f, field:e.target.value}))} style={{...inputStyle, marginBottom:0, flex:'1 1 180px'}}/>
            </div>
            <div style={cardStyle}>
                <h3 style={{margin:'0 0 10px', color:theme.navy}}>GST Audit Trail ({list.length})</h3>
                <p style={{fontSize:'0.8rem', color:theme.textMuted, margin:'0 0 12px'}}>Immutable record of every change to GST-sensitive fields. Retain for statutory audit.</p>
                <table style={{width:'100%', borderCollapse:'collapse', fontSize:'0.84rem'}}>
                    <thead><tr>
                        <th style={thStyle}>Time</th><th style={thStyle}>Entity</th><th style={thStyle}>Ref</th>
                        <th style={thStyle}>Field</th><th style={thStyle}>Old</th><th style={thStyle}>→ New</th>
                        <th style={thStyle}>User</th><th style={thStyle}>IP</th>
                    </tr></thead>
                    <tbody>
                        {list.map(a => (
                            <tr key={a._id}>
                                <td style={tdStyle}>{new Date(a.changedAt).toLocaleString('en-IN')}</td>
                                <td style={tdStyle}><strong>{a.entityType}</strong></td>
                                <td style={tdStyle}><code style={{fontSize:'0.72rem'}}>{a.entityRef || String(a.entityId).slice(-8)}</code></td>
                                <td style={tdStyle}>{a.fieldPath}</td>
                                <td style={tdStyle}><span style={{color:theme.danger, textDecoration:'line-through'}}>{JSON.stringify(a.oldValue)}</span></td>
                                <td style={tdStyle}><span style={{color:theme.success, fontWeight:'600'}}>{JSON.stringify(a.newValue)}</span></td>
                                <td style={tdStyle}>{a.changedBy || '—'}</td>
                                <td style={tdStyle}><code style={{fontSize:'0.72rem'}}>{a.ip || '—'}</code></td>
                            </tr>
                        ))}
                        {list.length === 0 && <tr><td colSpan="8" style={{...tdStyle, textAlign:'center', color:theme.textMuted, padding:'30px'}}>No audit entries yet</td></tr>}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ── Shared helpers ────────────────────────────────────────────────────
function Section({ title, rows, cols }) {
    if (!rows?.length) return (
        <div style={{...cardStyle, marginBottom:'12px'}}>
            <h4 style={{margin:'0 0 6px', color:theme.navy, fontSize:'0.92rem'}}>{title}</h4>
            <div style={{textAlign:'center', color:theme.textMuted, padding:'20px 0'}}>No records</div>
        </div>
    );
    return (
        <div style={{...cardStyle, marginBottom:'12px'}}>
            <h4 style={{margin:'0 0 10px', color:theme.navy, fontSize:'0.92rem'}}>{title}</h4>
            <div style={{overflowX:'auto'}}>
                <table style={{width:'100%', borderCollapse:'collapse', fontSize:'0.8rem'}}>
                    <thead><tr>{cols.map(c => <th key={c} style={thStyle}>{c}</th>)}</tr></thead>
                    <tbody>
                        {rows.map((r, i) => (
                            <tr key={i}>{cols.map(c => <td key={c} style={tdStyle}>{typeof r[c] === 'number' ? r[c].toLocaleString('en-IN') : (r[c] || '—')}</td>)}</tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function Modal({ title, onClose, children, wide }) {
    return (
        <div style={{position:'fixed', inset:0, backgroundColor:'rgba(15,23,42,0.55)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:9999}} onClick={onClose}>
            <div onClick={e=>e.stopPropagation()} style={{backgroundColor:'white', borderRadius:'14px', padding:'24px 28px', width:wide?'900px':'560px', maxWidth:'95vw', maxHeight:'90vh', overflow:'auto'}}>
                <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'14px'}}>
                    <h3 style={{margin:0, color:theme.navy}}>{title}</h3>
                    <button onClick={onClose} style={{background:'none', border:'none', cursor:'pointer', fontSize:'1.3rem'}}>×</button>
                </div>
                {children}
            </div>
        </div>
    );
}
